import { getFromStorage, setOnStorage } from '@libs/utils/helpers/handleStorage';

import { MatchMakerCache, MatchMakerDecision, MMResultCache } from '../types';
import { useApiMM } from '../api/useApiMM';
import { SHOffer, useSH } from '../../sortingHat';
import { MM_STORAGE } from '../../../pages/MatchMaker/constants';
import { getSBOfferBySlug } from '../../storyblok';

type useMMProps = {
  token?: string;
};

export const useMM = ({ token }: useMMProps) => {
  const { matchRequest } = useApiMM(token);
  const { decisionsList } = useSH();

  async function getComparedCards() {
    try {
      if (!token) throw new Error('Token is missing');
      const { monthlyExpense, benefit, annuity } = storedFormValues();

      const data = {
        monthlyExpenses: monthlyExpense,
        benefit: benefit,
        annuity: annuity,
      };

      const storageResult = getFromStorage<MMResultCache[]>(MM_STORAGE.RESULT);
      const cachedResult = storageResult?.find((item) => item.filter === JSON.stringify(data));

      if (cachedResult) {
        return cachedResult.result;
      }

      const response = await matchRequest(data);
      const decisionsResult = response?.matchMakerResult?.decisions;

      const formattedResult = await formatResponse(decisionsResult);

      setOnStorage('session', MM_STORAGE.RESULT, [
        ...(storageResult || []),
        {
          filter: JSON.stringify(data),
          result: formattedResult,
        },
      ]);

      return formattedResult;
    } catch (error) {
      console.error('[MM][COMPARE_CARDS]', error);
    }
  }

  async function formatResponse(mmDecisionList: MatchMakerDecision[]) {
    const updatedOfferList: SHOffer[] = [];

    for (const mmOffer of mmDecisionList) {
      let safeOffer = decisionsList.find((item) => item?.content?.slug === mmOffer.slug);

      if (!safeOffer) {
        const storyblokOffer = await getSBOfferBySlug(mmOffer.slug);
        if (!storyblokOffer) continue;
        safeOffer = storyblokOffer;
      }

      updatedOfferList.push({
        ...safeOffer,
        matchMaker: {
          ...mmOffer,
        },
      });
    }
    return updatedOfferList;
  }

  const storedFormValues = () => {
    const defaultValues = {
      monthlyExpense: 1000,
      annuity: false,
      benefit: 'cashback',
    };

    try {
      const cache = getFromStorage<MatchMakerCache>(MM_STORAGE.FORM);
      return {
        monthlyExpense: formatCurrencyToNumber(cache?.monthly_expense) ?? defaultValues.monthlyExpense,
        annuity: cache?.annuity ?? defaultValues.annuity,
        benefit: cache?.benefit ?? defaultValues.benefit,
      };
    } catch (error) {
      console.error('[MM][RETRIEVING_STORAGE]', error);
      return defaultValues;
    }
  };

  const formatCurrencyToNumber = (value?: string) => {
    if (!value) return;
    const sanitizedValue = value.replace(/R\$|\s/g, '');
    const numberValue = parseFloat(sanitizedValue.replace(/\./g, '').replace(',', '.'));
    return numberValue;
  };

  return {
    getComparedCards,
  };
};
